import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';

// Reusable single-select combobox with an in-dropdown search box. Matches the
// teal CustomSelect look but adds type-to-filter — used for long option lists
// (specialties, addresses, …). Self-contained: owns its open + search state.
//
// Props:
//   value        current value ('' = the "all"/placeholder option)
//   onChange(v)  called with the chosen value
//   options      [{ value, label }]  (strings are also accepted)
//   placeholder  label shown when nothing is selected (also the "all" row)
//   icon         optional lucide icon component rendered on the left
//   searchPlaceholder, className, testId
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  icon: Icon,
  className = '',
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Normalise options to { value, label }.
  const opts = useMemo(
    () => options.map((o) => (typeof o === 'object' ? o : { value: o, label: o })),
    [options]
  );

  const selected = opts.find((o) => o.value === value);
  const display = selected ? selected.label : placeholder;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return opts;
    return opts.filter((o) => String(o.label).toLowerCase().includes(term));
  }, [opts, q]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Focus the search box + reset the query when opening.
  useEffect(() => {
    if (open) { setQ(''); setTimeout(() => inputRef.current?.focus(), 0); }
  }, [open]);

  const pick = (v) => { onChange(v); setOpen(false); };

  return (
    <div ref={rootRef} className={`relative w-full sm:w-56 ${className}`} data-testid={testId ? `${testId}-container` : undefined}>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 pl-9 pr-8 py-2 text-xs sm:text-sm bg-white text-gray-700 rounded-xl border border-gray-200 shadow-sm hover:border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 cursor-pointer relative"
      >
        {Icon && <Icon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4ABDBD]" />}
        <span className="truncate text-left flex-1">{display}</span>
        <ChevronDown className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          data-testid={testId ? `${testId}-dropdown` : undefined}
          className="absolute z-[9999] left-0 mt-2 w-full min-w-[12rem] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
        >
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                data-testid={testId ? `${testId}-search` : undefined}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30"
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-[#4ABDBD] scrollbar-track-gray-100">
            {/* "All"/placeholder row */}
            <Option label={placeholder} active={!value} onClick={() => pick('')} />
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400" data-testid={testId ? `${testId}-empty` : undefined}>No matches</div>
            ) : (
              filtered.map((o, i) => (
                <Option
                  key={o.value ?? i}
                  label={o.label}
                  active={o.value === value}
                  onClick={() => pick(o.value)}
                  testId={testId ? `${testId}-option-${o.value ?? i}` : undefined}
                />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Option = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm rounded-lg mb-0.5 transition-colors ${
      active ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white' : 'text-gray-700 hover:bg-[#4ABDBD]/10'
    }`}
  >
    <span className="truncate">{label}</span>
    {active && <Check className="w-4 h-4 flex-shrink-0" />}
  </button>
);
